import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Mail, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderNumber}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (orderNumber) {
      fetchOrder();
    }
  }, [orderNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white text-xl">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Order Not Found</h2>
          <Link to="/">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <CheckCircle className="w-24 h-24 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-2xl text-purple-200 mb-2">Your order has been confirmed</p>
          <p className="text-yellow-400 text-xl font-semibold">Order #{order.order_number}</p>
        </div>

        <Card className="bg-green-500/10 border-green-500/50 mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Mail className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-white font-semibold">Confirmation email sent!</p>
                <p className="text-purple-200 text-sm">
                  We have sent an order confirmation to {order.customer.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-800/50 border-purple-600 mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Package className="mr-2" />
              Order Details
            </h2>

            <div className="space-y-4 mb-6">
              {order.items.map((item) => (
                <div key={`order-item-${item.id}-${item.size}`} className="flex gap-4 pb-4 border-b border-purple-600 last:border-0">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="text-white font-semibold">{item.name}</p>
                    <p className="text-purple-300 text-sm">Size: {item.size}</p>
                    <p className="text-purple-200 text-sm">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">${item.price.toFixed(2)}</p>
                    <p className="text-purple-300 text-sm">each</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-purple-600 pt-4">
              <div className="flex justify-between text-purple-200">
                <span>Subtotal</span>
                <span>${order.pricing.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-purple-200">
                <span>Shipping</span>
                <span>
                  {order.pricing.shipping === 0 ? (
                    <span className="text-green-400">FREE</span>
                  ) : (
                    `$${order.pricing.shipping.toFixed(2)}`
                  )}
                </span>
              </div>
              <div className="flex justify-between text-purple-200">
                <span>Tax</span>
                <span>${order.pricing.tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white text-xl font-bold pt-2 border-t border-purple-600">
                <span>Total</span>
                <span>${order.pricing.total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-purple-800/50 border-purple-600">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-white mb-4">Shipping Address</h3>
              <div className="text-purple-200 space-y-1">
                <p className="text-white font-semibold">{order.customer.name}</p>
                <p>{order.shipping_address.line1}</p>
                {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
                <p>
                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                </p>
                <p>{order.shipping_address.country}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-purple-800/50 border-purple-600">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold text-white mb-4">Contact Information</h3>
              <div className="text-purple-200 space-y-2">
                <p>
                  <span className="text-white font-semibold">Email:</span><br />
                  {order.customer.email}
                </p>
                {order.customer.phone && (
                  <p>
                    <span className="text-white font-semibold">Phone:</span><br />
                    {order.customer.phone}
                  </p>
                )}
                <p className="mt-4 text-sm">
                  <span className="text-white font-semibold">Payment Method:</span><br />
                  {order.payment.method === 'stripe' ? 'Credit Card (Stripe)' : 'PayPal'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-purple-800/50 border-purple-600 mb-8">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-white mb-4">What is Next?</h3>
            <div className="space-y-3 text-purple-200">
              <p className="flex items-start">
                <span className="text-yellow-400 mr-2 mt-1">✓</span>
                Your order is being processed and will ship within 5-10 business days
              </p>
              <p className="flex items-start">
                <span className="text-yellow-400 mr-2 mt-1">✓</span>
                You will receive a shipping confirmation email with tracking information
              </p>
              <p className="flex items-start">
                <span className="text-yellow-400 mr-2 mt-1">✓</span>
                Questions? Contact us at daizyinfused@gmail.com
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-8">
              <Home className="mr-2" />
              Return Home
            </Button>
          </Link>
          <Link to="/products">
            <Button 
              variant="outline"
              className="border-purple-600 text-purple-200 hover:bg-purple-800 px-8"
            >
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
